import { PixoPt, PixoSpatial, PixoSpatialCoord } from "../../model/Pixospatial";
import { AnyHandler, SimpleAVHandler } from "../../types/@types/sl-v-types";
import { D3SelectedElement, D3SelectedGroup, EventMap, IDrawContext, IDrawer, IMapInteraction, MakeInteractionGroup, RemoveInteractionGroup } from "./draw-pane-utils";

export type ROICorners = { from: PixoSpatialCoord, to: PixoSpatialCoord }

import * as d3 from "d3";
export class RectangleROI implements IMapInteraction, IDrawContext {

  public drawRect!: D3SelectedGroup;
  public context: IDrawer;
  private handler!: AnyHandler;
  public interactionGroup?: D3SelectedGroup;
  public itemElement: D3SelectedElement;

  public eventMap?: EventMap;
  public enabled: boolean = false;
  public pixo: PixoSpatial;

  private handledTouch: boolean = false;

  public WindowUpHandler: SimpleAVHandler<MouseEvent>;

  public from?: PixoSpatialCoord;
  public to?: PixoSpatialCoord;
  public autoEnd: boolean = true;
  public get drawWidth() {
    return !!this.context ? this.context?.drawWidth : 0;
  }
  public get drawHeight() {
    return !!this.context ? this.context?.drawHeight : 0;
  }

  Enable(context: IDrawer, drawRect: D3SelectedGroup, handler: AnyHandler, params: any): void {
    this.autoEnd = params?.hasOwnProperty('autoEnd') ? params.autoEnd : true;
    if(!params) this.autoEnd = true;
    if (this.enabled) return;
    this.drawRect = drawRect;
    this.handler = handler;
    this.pixo = context.spatialContext;
    this.context = context;
    this.from = undefined;
    this.to = undefined;

    this.eventMap = {
      touchStart: this.TouchStart.bind(this),
      touchMove: this.TouchMove.bind(this),
      touchEnd: this.TouchEnd.bind(this),
      mouseDown: this.MouseDown.bind(this),
      mouseMove: this.MouseMove.bind(this),
      mouseUp: this.MouseUp.bind(this),
      mouseEnter: this.MouseEnter.bind(this)
    };
    this.interactionGroup = MakeInteractionGroup(this, this.drawRect, this.eventMap);
    this.itemElement = this.interactionGroup!.append('rect')
      .attr('x', '0')
      .attr('y', '0')
      .attr('width', 0)
      .attr('height', 0)
      .classed('roi', true);
    this.itemElement.on('mouseup', this.MouseUp.bind(this));
    this.itemElement.on('mousemove',()=>{
      if (!this.enabled) return;

      const coord = this.GetMouseCoord(d3.event);

      if (this.from === null) {
        if ((d3.event.buttons & 1) !== 1) {
          // left button not pressed;
          return;
        } else {
          this.ROIFrom(coord);
          return;
        }
      }
      this.ROITo(coord);
    });
    this.enabled = true;

  }
  Begin() {
    this.WindowUpHandler = (event: MouseEvent) => {
      if ((event.buttons & 1) === 0) {
        this.HandleROIMouseEvent('clear', event);
        window.removeEventListener('mouseup', this.WindowUpHandler);
      }
    }
  }
  End() {
    this.Clear();
    if (this.autoEnd) {
      this.Disable();
    }
  }
  TouchStart(event) {
    if (!this.enabled) return;

    this.handledTouch = true;
    this.Begin();
    const coord = this.GetTouchPoint(d3.event.touches);
    if (coord) {
      this.ROIFrom(coord);
    }
  }
  TouchMove(event) {
    if (!this.enabled) return;

    const coord = this.GetTouchPoint(d3.event.changedTouches);
    if (!coord) return;
    this.ROITo(coord);
  }
  TouchEnd(event) {
    if (!this.enabled) return;

    if (this.from === null) {
      return;
    }
    const coord = this.GetTouchPoint(d3.event.changedTouches)
    if (coord) {
      this.ROITo(coord);
    }
    this.handler({ from: this.from, to: this.to });
    this.End();

  }

  MouseUp(event) {
    if (!this.enabled) return;

    if (this.from === null) {
      this.End();
      return;
    }
    const coord = this.GetMouseCoord(d3.event);
    this.ROITo(coord);
    this.handler({ from: this.from, to: this.to });
    this.End();

  }
  MouseDown(event) {
    if (!this.enabled) return;

    this.Begin();
    const coord = this.GetMouseCoord(d3.event);
    this.ROIFrom(coord);
  }
  MouseEnter(event) {
    if (!this.enabled) return;

    if (d3.event.buttons > 0) {
      this.MouseMove();
    } else {
      this.Clear();
    }
  };
  MouseMove() {
    if (!this.enabled) return;

    const coord = this.GetMouseCoord(d3.event);

    if (this.from === null) {
      if ((d3.event.buttons & 1) !== 1) {
        // left button not pressed;
        return;
      } else {
        this.ROIFrom(coord);
        return;
      }
    }
    this.ROITo(coord);
  }

  GetMouseCoord(event): PixoSpatialCoord {
    const pt = { x: event.offsetX, y: event.offsetY };
    pt.x += this.drawRect!.node()!.clientLeft;
    pt.y += this.drawRect!.node()!.clientTop;
    return this.pixo.PixoSpatialCoord(this.pixo.PixoPt(pt));
  }
  HandleROIMouseEvent(mode: 'clear' | 'start' | 'move' | 'end', event) {
    if (mode === 'clear') {
      this.from = undefined;
      this.to = undefined;
      this.UpdateROI(-1, -1, 0, 0);
      return;
    }
    const pt = { x: event.offsetX, y: event.offsetY };
    pt.x += this.drawRect!.node()!.clientLeft;
    pt.y += this.drawRect!.node()!.clientTop;
    if (event.buttons === 0) {
      if (mode !== 'end') {
        return;
      }
    }
    const point = this.pixo.PixoPt(pt);
    const downCoord = this.pixo.PixoSpatialCoord(point);

    this.handler({
      type: 'mouse',
      roiElement: this.itemElement,
      location: downCoord,
      action: mode,
      left: (event.buttons & 1) === 1,
      middle: (event.buttons & 4) === 4,
      right: (event.buttons & 2) === 2
    });
    this.End();
  }
  GetTouchPoint(touches): undefined | PixoSpatialCoord {
    if (!touches.item) return undefined;
    const rect: DOMRect = (this.drawRect.node() as SVGElement).getBoundingClientRect();
    const touchPt = this.pixo.PixoPt({
      x: ((touches.item(0) as Touch).pageX - rect.left),
      y: ((touches.item(0) as Touch).pageY - rect.top)
    });

    return this.pixo.PixoSpatialCoord(touchPt);
  }
  HandleROITouchEvent(mode: 'start' | 'move' | 'end', event: any) {
    const touches: TouchList = (mode === 'start') ? event.touches : event.changedTouches;

    if (!touches.item) return;
    const touchPt = this.pixo.PixoPt({
      x: (touches.item(0)!.pageX - this.drawRect!.node()!.clientLeft),
      y: (touches.item(0)!.pageY - this.drawRect!.node()!.clientTop)
    });
    const touchCoord = this.pixo.PixoSpatialCoord(touchPt);
    const start = {
      x: Number(this.itemElement.attr('x')),
      y: Number(this.itemElement.attr('y'))
    };
    if ((mode === 'end')) {
      const end: PixoPt = this.pixo.PixoPt(
        {
          x: (Number(this.itemElement.attr('width')) + start.x),
          y: (Number(this.itemElement.attr('height')) + start.y)
        }
      );

      const endCoord = this.pixo.PixoSpatialCoord(end);
      this.handler({
        type: 'touch',
        roiElement: this.itemElement,
        location: endCoord,
        action: 'end',
        left: touches.length > 0,
        middle: touches.length > 2,
        right: touches.length > 1
      });
      return;
    }
    let touchCoord2: any = null;
    if (touches.length > 1) {
      const touchPt2 = this.pixo.PixoPt({
        x: touches.item(1)!.pageX - this.drawRect!.node()!.clientLeft,
        y: touches.item(1)!.pageY - this.drawRect!.node()!.clientTop
      });
      touchCoord2 = this.pixo.PixoSpatialCoord(touchPt2);
    }

    this.handler({
      type: 'touch',
      roiElement: this.itemElement,
      location: touchCoord,
      location2: touchCoord2,
      action: mode,
      left: touches.length > 0,
      middle: touches.length > 2,
      right: touches.length > 1
    });
  }
  private ROIFrom(coord: PixoSpatialCoord) {
    this.from = coord;
    this.to = coord;
    this.UpdatePoints();
  }
  private UpdatePoints() {
    if (!this.from) return;
    const x1 = this.from.pixo.x;
    const y1 = this.from.pixo.y;
    const x2 = (!!this.to) ? this.to.pixo.x : this.from.pixo.x;
    const y2 = (!!this.to) ? this.to.pixo.y : this.from.pixo.y;
    const minX = Math.min(x1, x2);
    const minY = Math.min(y1, y2);
    const maxX = Math.max(x1, x2);
    const maxY = Math.max(y1, y2);
    const width = maxX - minX;
    const height = maxY - minY;
    this.UpdateROI(minX, minY, width, height);
  }
  private ROITo(coord: PixoSpatialCoord) {
    this.to = coord;
    this.UpdatePoints();
  }
  public GetPoints(): any {
    return { from: this.from, to: this.to };
  }
  public Clear() {
    this.from = undefined;
    this.to = undefined;
    this.UpdateROI(-1, -1, 0, 0);
  }
  Disable() {
    this.enabled = false;
    this.from = undefined;
    this.to = undefined;

    if (!this.interactionGroup) return;
    RemoveInteractionGroup(this.interactionGroup, this.eventMap!);
    window.removeEventListener('mouseup', this.WindowUpHandler);
    this.itemElement?.remove();
    delete this.itemElement;
    delete this.eventMap;
  }
  TogglePausing(onOrOff: boolean): void {
    if (onOrOff === true) {
      this.enabled = false;
    }
    if (!(onOrOff && this.enabled)) {
      this.enabled = !!this.drawRect;
    }
  }

  UpdateROI(x, y, width, height) {
    if (!this.itemElement) return;
    this.itemElement
      .attr('x', x)
      .attr('y', y)
      .attr('width', width)
      .attr('height', height)
      .classed('hidden', false)
      .style('filter', 'url(#glow)');
  }

}
